import { Button, notify, Switch, useConfirmModal } from '@affine/component';
import { generateSubscriptionCallbackLink } from '@affine/core/components/hooks/affine/use-subscription-notify';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { AuthService, SubscriptionService } from '@affine/core/modules/cloud';
import { GlobalDialogService } from '@affine/core/modules/dialogs';
import { UrlService } from '@affine/core/modules/url';
import {
  SubscriptionPlan,
  SubscriptionRecurring,
  type TariffsQuery,
} from '@affine/graphql';
import { i18nTime, useI18n } from '@affine/i18n';
import { DoneIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import bytes from 'bytes';
import clsx from 'clsx';
import { nanoid } from 'nanoid';
import { type ReactNode, useMemo, useState } from 'react';

import { CheckoutSlot } from './checkout-slot';
import { CloudPlanLayout } from './layout';
import * as styles from './style.css';

export type Tariffs = NonNullable<TariffsQuery['tariffs']>;
type Tier = Tariffs['tiers'][number];

const formatMoney = (minor: number, currency: string) => {
  const value = minor / 100;
  const formatted = value.toLocaleString('ru-RU', {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  });
  return currency === 'rub' ? `${formatted} ₽` : `${formatted} ${currency}`;
};

const TariffCard = ({
  name,
  price,
  priceCaption,
  benefits,
  action,
  current,
  highlight,
}: {
  name: ReactNode;
  price: ReactNode;
  priceCaption?: ReactNode;
  benefits: ReactNode[];
  action: ReactNode;
  current?: boolean;
  highlight?: boolean;
}) => (
  <div
    data-current={current}
    className={clsx(
      highlight ? styles.proPlanCard : styles.planCard,
      styles.tariffGridCard
    )}
  >
    <div className={styles.planCardBorderMock} />
    <div className={styles.planTitle}>
      <div style={{ paddingBottom: 12 }}>
        <section className={styles.planTitleName}>{name}</section>
        <section className={styles.planTitleTitle}>
          {price}
          {priceCaption ? (
            <span className={styles.planTitleTitleCaption}>{priceCaption}</span>
          ) : null}
        </section>
      </div>
      {action}
    </div>
    <div className={styles.planBenefits}>
      <ul className={styles.planBenefitGroup}>
        {benefits.map((benefit, index) => (
          <li className={styles.planBenefit} key={index}>
            <div className={styles.planBenefitIcon}>
              <DoneIcon />
            </div>
            <div className={styles.planBenefitText}>{benefit}</div>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const TierAction = ({
  tier,
  recurring,
}: {
  tier: Tier;
  recurring: SubscriptionRecurring;
}) => {
  const t = useI18n();
  const authService = useService(AuthService);
  const globalDialogService = useService(GlobalDialogService);
  const urlService = useService(UrlService);
  const subscriptionService = useService(SubscriptionService);
  const loggedIn = useLiveData(authService.session.status$) === 'authenticated';
  const account = useLiveData(authService.session.account$);
  const subscription = useLiveData(subscriptionService.subscription.pro$);
  const { openConfirmModal } = useConfirmModal();

  const isCurrentTier = !!subscription && subscription.tier === tier.id;
  const isCurrent = isCurrentTier && subscription?.recurring === recurring;

  const cancel = useAsyncCallback(async () => {
    await subscriptionService.subscription.cancelSubscription(nanoid());
    notify.success({ title: t['com.affine.payment.tariffs.canceled']() });
  }, [subscriptionService, t]);

  const resume = useAsyncCallback(async () => {
    await subscriptionService.subscription.resumeSubscription(nanoid());
  }, [subscriptionService]);

  if (!loggedIn) {
    return (
      <Button
        className={styles.planAction}
        variant="primary"
        onClick={() => globalDialogService.open('sign-in', {})}
      >
        {t['com.affine.payment.tariffs.choose']()}
      </Button>
    );
  }

  if (isCurrent) {
    return subscription?.canceledAt ? (
      <Button className={styles.planAction} variant="primary" onClick={resume}>
        {t['com.affine.payment.tariffs.resume']()}
      </Button>
    ) : (
      <Button
        className={styles.planAction}
        onClick={() =>
          openConfirmModal({
            title: t['com.affine.payment.tariffs.cancel-title'](),
            description: t['com.affine.payment.tariffs.cancel-description']({
              end: subscription?.end
                ? i18nTime(subscription.end, {
                    absolute: { accuracy: 'day' },
                  })
                : '',
            }),
            confirmText: t['com.affine.payment.tariffs.cancel'](),
            confirmButtonOptions: { variant: 'error' },
            onConfirm: cancel,
          })
        }
      >
        {t['com.affine.payment.tariffs.cancel-short']()}
      </Button>
    );
  }

  return (
    <CheckoutSlot
      checkoutOptions={{
        plan: SubscriptionPlan.Pro,
        recurring,
        variant: null,
        coupon: null,
        successCallbackLink: generateSubscriptionCallbackLink(
          account ?? null,
          SubscriptionPlan.Pro,
          recurring,
          '',
          urlService.getClientScheme()
        ),
        args: { tier: tier.id },
      }}
      renderer={({ onClick, loading }) => (
        <Button
          className={styles.planAction}
          variant="primary"
          loading={loading}
          onClick={onClick}
        >
          {subscription
            ? t['com.affine.payment.tariffs.switch']()
            : t['com.affine.payment.tariffs.choose']()}
        </Button>
      )}
    />
  );
};

/**
 * Fork billing: tariff cards (Free + paid tiers + special conditions) served
 * by the yookassa payment provider.
 */
export const TariffPlans = ({ tariffs }: { tariffs: Tariffs }) => {
  const t = useI18n();
  const subscriptionService = useService(SubscriptionService);
  const loggedIn =
    useLiveData(useService(AuthService).session.status$) === 'authenticated';
  const subscription = useLiveData(subscriptionService.subscription.pro$);
  const [recurring, setRecurring] = useState<SubscriptionRecurring>(
    subscription?.recurring === SubscriptionRecurring.Yearly
      ? SubscriptionRecurring.Yearly
      : SubscriptionRecurring.Monthly
  );
  const yearly = recurring === SubscriptionRecurring.Yearly;
  const storage = bytes.format(tariffs.storageQuota) ?? '';
  const currentTier = tariffs.tiers.find(
    tier => tier.id === subscription?.tier
  );

  const caption = useMemo(() => {
    if (!loggedIn) {
      return t['com.affine.payment.subtitle-not-signed-in']();
    }
    if (!subscription || !currentTier) {
      return t['com.affine.payment.tariffs.current']({ plan: 'Free' });
    }
    const end = subscription.end
      ? i18nTime(subscription.end, { absolute: { accuracy: 'day' } })
      : '';
    return subscription.canceledAt
      ? t['com.affine.payment.tariffs.current-canceled']({
          plan: currentTier.name,
          end,
        })
      : t['com.affine.payment.tariffs.current-renews']({
          plan: currentTier.name,
          end,
        });
  }, [currentTier, loggedIn, subscription, t]);

  const toggle = (
    <div className={styles.recurringToggleWrapper}>
      <div className={styles.recurringToggleRecurring}>
        <span>
          {t['com.affine.payment.cloud.pricing-plan.toggle-billed-yearly']()}
        </span>
      </div>
      <Switch
        checked={yearly}
        onChange={checked =>
          setRecurring(
            checked
              ? SubscriptionRecurring.Yearly
              : SubscriptionRecurring.Monthly
          )
        }
      />
    </div>
  );

  const common = [t['com.affine.payment.tariffs.storage']({ storage })];

  const cards = (
    <>
      <div className={styles.tariffGrid}>
        <TariffCard
          name="Free"
          price={formatMoney(0, tariffs.currency)}
        // keeps the action button aligned with the paid tiers
        priceCaption={'\u00a0'}
          current={loggedIn && !subscription}
          benefits={[
            t['com.affine.payment.tariffs.members']({
              count: String(tariffs.freeMemberLimit),
            }),
            t['com.affine.payment.tariffs.workspaces']({
              count: String(tariffs.freeMaxWorkspaces),
            }),
            ...common,
          ]}
          action={
            <Button className={styles.planAction} disabled>
              {loggedIn && !subscription
                ? t['com.affine.payment.current-plan']()
                : 'Free'}
            </Button>
          }
        />
        {tariffs.tiers.map((tier, index) => (
          <TariffCard
            key={tier.id}
            name={tier.name}
            highlight={index === 1}
            current={subscription?.tier === tier.id}
            price={formatMoney(
              yearly ? tier.yearlyPrice : tier.monthlyPrice,
              tariffs.currency
            )}
            priceCaption={
              yearly
                ? t['com.affine.payment.tariffs.per-year']()
                : t['com.affine.payment.tariffs.per-month']()
            }
            benefits={[
              t['com.affine.payment.tariffs.members']({
                count: String(tier.memberLimit),
              }),
              t['com.affine.payment.tariffs.unlimited-workspaces'](),
              ...common,
            ]}
            action={<TierAction tier={tier} recurring={recurring} />}
          />
        ))}
      </div>
      <div className={styles.tariffSpecial}>
        <div>
          <div className={styles.tariffSpecialTitle}>
            {t['com.affine.payment.tariffs.special']()} ·{' '}
            {t['com.affine.payment.tariffs.special-price']()}
          </div>
          <div className={styles.tariffSpecialText}>
            {t['com.affine.payment.tariffs.special-description']()}
          </div>
        </div>
        <Button
          onClick={() => {
            if (tariffs.contactUrl) {
              window.open(tariffs.contactUrl, '_blank', 'noopener');
            }
          }}
        >
          {t['com.affine.payment.tariffs.contact']()}
        </Button>
      </div>
    </>
  );

  return (
    <CloudPlanLayout caption={<p>{caption}</p>} toggle={toggle} body={cards} />
  );
};
